// @flow

import _ from 'lodash';

import ApiObject from '../ApiObject';

// -- utils
import BookingUtils from '../../utils/BookingUtils';
import DateUtils from '../../utils/DateUtils';

// -- constants
import ApiConstants from '../../constants/ApiConstants';
import BookingConstants from '../../constants/BookingConstants';

// -- entities
import BookingSettings from './BookingSettings';
import BookingDaySettings from './BookingDaySettings';
import BookingSettingsRoom from './BookingSettingsRoom';
import BookingDayRoomSettings from './BookingDayRoomSettings';
import BookingSlotCapacitySettings from './BookingSlotCapacitySettings';

export default class BookingSettingsPeriod extends ApiObject {

  // BookingConstants.BookingPeriod
  period: number = 0;

  /**
   * The average duration of the meal for the period.
   * In milliseconds
   * @type {number}
   */
  averageDurationOfMeal: number = DateUtils.HOUR_AS_MILLISECONDS;

  monday: BookingDaySettings = new BookingDaySettings();

  tuesday: BookingDaySettings = new BookingDaySettings();

  wednesday: BookingDaySettings = new BookingDaySettings();

  thursday: BookingDaySettings = new BookingDaySettings();

  friday: BookingDaySettings = new BookingDaySettings();

  saturday: BookingDaySettings = new BookingDaySettings();

  sunday: BookingDaySettings = new BookingDaySettings();

  // -- serialization
  postJsonAssignation(data: Object) {
    this.monday = new BookingDaySettings();
    this.monday.fromJson(data.monday);

    this.tuesday = new BookingDaySettings();
    this.tuesday.fromJson(data.tuesday);

    this.wednesday = new BookingDaySettings();
    this.wednesday.fromJson(data.wednesday);

    this.thursday = new BookingDaySettings();
    this.thursday.fromJson(data.thursday);

    this.friday = new BookingDaySettings();
    this.friday.fromJson(data.friday);

    this.saturday = new BookingDaySettings();
    this.saturday.fromJson(data.saturday);

    this.sunday = new BookingDaySettings();
    this.sunday.fromJson(data.sunday);
  }

  // -- Basic utils

  getSettingsForDay(day: number): ?BookingDaySettings {
    switch (day) {
      case ApiConstants.Day.MONDAY:
        return this.monday;
      case ApiConstants.Day.TUESDAY:
        return this.tuesday;
      case ApiConstants.Day.WEDNESDAY:
        return this.wednesday;
      case ApiConstants.Day.THURSDAY:
        return this.thursday;
      case ApiConstants.Day.FRIDAY:
        return this.friday;
      case ApiConstants.Day.SATURDAY:
        return this.saturday;
      case ApiConstants.Day.SUNDAY:
        return this.sunday;
      default:
        return null;
    }
  }

  setSettingsForDay(bookingDaySettings: BookingDaySettings, day: number) {
    switch (day) {
      case ApiConstants.Day.MONDAY:
        this.monday = bookingDaySettings;
        break;
      case ApiConstants.Day.TUESDAY:
        this.tuesday = bookingDaySettings;
        break;
      case ApiConstants.Day.WEDNESDAY:
        this.wednesday = bookingDaySettings;
        break;
      case ApiConstants.Day.THURSDAY:
        this.thursday = bookingDaySettings;
        break;
      case ApiConstants.Day.FRIDAY:
        this.friday = bookingDaySettings;
        break;
      case ApiConstants.Day.SATURDAY:
        this.saturday = bookingDaySettings;
        break;
      case ApiConstants.Day.SUNDAY:
        this.sunday = bookingDaySettings;
        break;
      default:
        return null;
    }
  }

  // validation

  isValid(bookingSettings: BookingSettings): boolean {
    let res = true;

    if (this.averageDurationOfMeal === 0) {
      console.log('BookingSettingsPeriod is invalid (averageDurationOfMeal)');
      return false;
    }
    _.forIn(ApiConstants.Day, (day: number) => {
      const settingsForDay = this.getSettingsForDay(day);

      const totalCapacityForDay = this.getTotalCapacityForDay(day);
      if (totalCapacityForDay > bookingSettings.maximumCapacity) {
        res = false;
        console.log(`BookingSettingsPeriod is invalid capacity:${totalCapacityForDay} max:${bookingSettings.maximumCapacity}`);
        return false;
      }

      if (settingsForDay.isOpen() && !settingsForDay.isValid(bookingSettings)) {
        res = false;
        console.log(`BookingSettingsPeriod is invalid period:${this.period} day: ${day}`);
        return false; // quit the loop
      }
    });

    return res;
  }

  // -- Status utils

  getStateForDay(day: number): number {
    const settingsForDay = this.getSettingsForDay(day);

    if (_.isNull(settingsForDay)) {
      console.error('[getStateForDay] Invalid day', day);
      return 0;
    }

    return settingsForDay.state;
  }

  setStateForDay(status: number, day: number) {
    const bookingDaySettings:BookingDaySettings = this.getSettingsForDay(day);

    bookingDaySettings.state = status;

    this.setSettingsForDay(bookingDaySettings, day);
  }

  // -- average duration of meal

  getAverageDurationOfMealForDay(day: number): number {
    const settingsForDay = this.getSettingsForDay(day);

    if (_.isNull(settingsForDay)) {
      console.error('[getAverageDurationOfMealForDay] Invalid day', day);
      return 0;
    }

    return settingsForDay.averageDurationOfMeal;
  }

  setAverageDurationOfMealForDay(averageDuration: number, day: number) {
    const bookingDaySettings:BookingDaySettings = this.getSettingsForDay(day);

    bookingDaySettings.averageDurationOfMeal = averageDuration;

    this.setSettingsForDay(bookingDaySettings, day);
  }

  // -- opening hour utils

  getOpeningHourForDay(day: number): number {
    const settingsForDay = this.getSettingsForDay(day);

    if (_.isNull(settingsForDay)) {
      console.error('[getOpeningHourForDay] Invalid day', day);
      return 0;
    }

    return settingsForDay.openingHour;
  }

  setOpeningHourForDay(hour: number, day: number) {
    console.log('set opening hour', hour);
    const bookingDaySettings:BookingDaySettings = this.getSettingsForDay(day);

    bookingDaySettings.openingHour = hour;

    this.setSettingsForDay(bookingDaySettings, day);
  }

  // -- capacity
  getRoomCapacityForDay(room: BookingSettingsRoom, day: number) {
    const daySettings:BookingDaySettings = this.getSettingsForDay(day);
    return daySettings.getRoomSettings(room).capacity;
  }

  /**
   * Returns the capacity of all the rooms combined.
   * @param day
   */
  getTotalCapacityForDay(day: number): number {
    const daySettings:BookingDaySettings = this.getSettingsForDay(day);
    return daySettings.getTotalCapacity();
  }

  setRoomCapacityForDay(room: BookingSettingsRoom, capacity: number, day: number) {
    const daySettings:BookingDaySettings = this.getSettingsForDay(day);

    const roomSettings:BookingDayRoomSettings = daySettings.getRoomSettings(room);
    roomSettings.capacity = capacity;
    roomSettings.roomId = room.id;

    daySettings.setRoomSettings(roomSettings);

    this.setSettingsForDay(daySettings, day);
  }

  // -- closing hour utils
  getClosingHourForDay(day: number): number {
    return this.getSettingsForDay(day).closingHour;
  }

  setClosingHourForDay(hour: number, day: number) {
    console.log('set closing hour', hour);
    const bookingDaySettings:BookingDaySettings = this.getSettingsForDay(day);

    bookingDaySettings.closingHour = hour;

    this.setSettingsForDay(bookingDaySettings, day);
  }

  isValidHourForDay(hour: Object, day: number): boolean {
    const opening = this.getOpeningHourForDay(day);
    const closing = this.getClosingHourForDay(day);
    return hour.unix() * 1000 >= opening && hour.unix() * 1000 <= closing;
  }

  isOpeningHoursValidForDay(day: number): boolean {
    const opening = this.getOpeningHourForDay(day);
    const closing = this.getClosingHourForDay(day);

    return opening < closing;
  }

  // -- specifics

  getAllBookingSlotCapacitySettings() {
    let list = [];

    list = list.concat(this.getSettingsForDay(ApiConstants.Day.MONDAY)
      .getSpecificsSettingsForRooms(ApiConstants.Day.MONDAY));

    list = list.concat(this.getSettingsForDay(ApiConstants.Day.TUESDAY)
      .getSpecificsSettingsForRooms(ApiConstants.Day.TUESDAY));

    list = list.concat(this.getSettingsForDay(ApiConstants.Day.WEDNESDAY)
      .getSpecificsSettingsForRooms(ApiConstants.Day.WEDNESDAY));

    list = list.concat(this.getSettingsForDay(ApiConstants.Day.THURSDAY)
      .getSpecificsSettingsForRooms(ApiConstants.Day.THURSDAY));

    list = list.concat(this.getSettingsForDay(ApiConstants.Day.FRIDAY)
      .getSpecificsSettingsForRooms(ApiConstants.Day.FRIDAY));

    list = list.concat(this.getSettingsForDay(ApiConstants.Day.SATURDAY)
      .getSpecificsSettingsForRooms(ApiConstants.Day.SATURDAY));

    list = list.concat(this.getSettingsForDay(ApiConstants.Day.SUNDAY)
      .getSpecificsSettingsForRooms(ApiConstants.Day.SUNDAY));

    return list;
  }

  addBookingSlotCapacitySettings(bookingSlotCapacitySetting: BookingSlotCapacitySettings) {
    const daySettings:BookingDaySettings = this.getSettingsForDay(bookingSlotCapacitySetting.day);
    daySettings.addBookingSlotCapacitySettings(bookingSlotCapacitySetting);
    this.setSettingsForDay(daySettings, bookingSlotCapacitySetting.day);
  }

  updateBookingSlotCapacitySettings(old: BookingSlotCapacitySettings,
                                    bookingSlotCapacitySetting: BookingSlotCapacitySettings) {
    // remove the old one that is on another day / room.
    this.deleteBookingSlotCapacitySettings(old);

    const daySettings:BookingDaySettings = this.getSettingsForDay(bookingSlotCapacitySetting.day);
    daySettings.updateBookingSlotCapacitySettings(bookingSlotCapacitySetting);
    this.setSettingsForDay(daySettings, bookingSlotCapacitySetting.day);
  }

  deleteBookingSlotCapacitySettings(bookingSlotCapacitySetting: BookingSlotCapacitySettings) {
    const daySettings:BookingDaySettings = this.getSettingsForDay(bookingSlotCapacitySetting.day);
    daySettings.deleteBookingSlotCapacitySettings(bookingSlotCapacitySetting);
    this.setSettingsForDay(daySettings, bookingSlotCapacitySetting.day);
  }

  /**
   * Returns the first day with slots. Used to set the default day of a BookingSlotCapacitySettings
   *
   * @param slotInterval
   * @returns {number}
   */
  getFirstDayWithSlots(slotInterval: number): number {
    let dayResult = null;
    _.forIn(ApiConstants.Day, (day: number) => {
      const daySettings:BookingDaySettings = this.getSettingsForDay(day);
      if (daySettings.state === BookingConstants.State.OPEN) {

        const slots = BookingUtils.generateSlotsForInterval(
          daySettings.openingHour,
          daySettings.closingHour,
          slotInterval
        );

        if (!_.isEmpty(slots)) {
          dayResult = day;
          return false;
        }
      }
    });

    return dayResult;
  }

  /**
   * Returns true if the period have all its days deactivated.
   *
   * Used because we don't display the specifics if all the days are deactivated
   * @returns {boolean}
   */
  isAllDaysDisabled(): boolean {
    let res = true;
    _.each(ApiConstants.Day, (value) => {
      const daySettings:BookingDaySettings = this.getSettingsForDay(value);
      if (daySettings.state === BookingConstants.State.OPEN) {
        res = false;
        return false;
      }
    });
    return res;
  }

  isEnableForDay(day: number): boolean {
    const daySettings:BookingDaySettings = this.getSettingsForDay(day);

    if (daySettings.state === BookingConstants.State.OPEN) {
      return true;
    }

    return false;
  }

}



// WEBPACK FOOTER //
// ./sdk/scripts/entities/booking/BookingSettingsPeriod.js